"use client";

import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  createTRPCClient,
  httpBatchLink,
  loggerLink,
  type TRPCLink
} from "@trpc/client";
import { createTRPCContext } from "@trpc/tanstack-react-query";
import type { AppRouter } from "@watt/api/routes";
import { env } from "@watt/common/config/env";
import { useState } from "react";
import superjson from "superjson";
import { TRPC_LOGS, tRPCBaseUrl } from "./config.js";
import { getQueryClient } from "./get-query-client.js";

export const { TRPCProvider, useTRPC } = createTRPCContext<AppRouter>();

/** T6 seam: future list-response decryption belongs here. */
const tableResponseLink: TRPCLink<AppRouter> =
  () =>
  ({ next, op }) =>
    next(op);

interface TRPCReactProviderProps {
  readonlyHeaders?: Headers;
  children: React.ReactNode;
}

/**
 * This is the client-side tRPC client for hook usage
 * via trpcClient.books.useQuery(...)
 * @param props
 * @returns
 */
export function TRPCReactProvider({
  children,
  readonlyHeaders
}: TRPCReactProviderProps) {
  const queryClient = getQueryClient();

  const [trpcClient] = useState(() =>
    createTRPCClient<AppRouter>({
      links: [
        loggerLink<AppRouter>({
          enabled: opts =>
            (env.NODE_ENV === "development" ||
              (opts.direction === "down" && opts.result instanceof Error)) &&
            TRPC_LOGS
        }),
        tableResponseLink,
        httpBatchLink({
          async headers() {
            // Stephen: We need to set the headers in development as the headers are not copied over for local development
            // Vercel does this automatically for production and if we try to do it in production it results in an error
            if (process.env.NODE_ENV === "development") {
              const headers = new Headers(readonlyHeaders);
              headers.set("x-trpc-source", "crm-react");
              return headers;
            }

            const headers = new Headers();
            if (readonlyHeaders) {
              for (const [key, value] of readonlyHeaders.entries()) {
                headers.append(key, value);
              }
            }
            headers.set("x-trpc-source", "crm-react");
            return headers;
          },
          methodOverride: "POST",
          transformer: superjson,
          url: `${tRPCBaseUrl()}/api/trpc`
        })
      ]
    })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider queryClient={queryClient} trpcClient={trpcClient}>
        {children}
      </TRPCProvider>
      <ReactQueryDevtools buttonPosition="bottom-left" initialIsOpen={false} />
    </QueryClientProvider>
  );
}
